import { saveKeywordLog, saveLanguageLog, saveMenuLog, saveNavigationLog, savePoiLog, saveSessionLog } from '@/services/operations/logging';
import type { KeywordLog, LanguageLog, MenuLog, NavigationLog, PoiContent, PoiLog, SessionLog } from '@/services/dto';
import { PATHS } from '@/router/paths';
import { BuildingMenuType } from '@/enums';
import { useConfigStore } from '@/stores/configStore';

type Nullable<T> = T | null;

type ContextIds = {
    floorId: string;
    storeId: string;
    buildingId: string;
    poiList: PoiContent[] | null;
};

type PoiLocationRef = Pick<PoiContent, 'studioFloorId' | 'studioBuildingId' | 'name'>;

type SessionState = {
    isActive: boolean;
    sessionStartAt: number; // epoch ms (first time become active)
    lastActiveStartAt: Nullable<number>;
    totalActiveMs: number;
    lastLocale: string;
    lastContext: Nullable<ContextIds>;
};

type LogQueueItem =
    | { type: 'session'; payload: SessionLog }
    | { type: 'menu'; payload: MenuLog }
    | { type: 'poi'; payload: PoiLog }
    | { type: 'keyword'; payload: KeywordLog }
    | { type: 'language'; payload: LanguageLog }
    | { type: 'navigation'; payload: NavigationLog };

class TrackingServiceImpl {
    private static _instance: TrackingServiceImpl | null = null;

    static get instance(): TrackingServiceImpl {
        if (!this._instance) this._instance = new TrackingServiceImpl();
        return this._instance;
    }

    private session: SessionState | null = null;
    private isInHomeBase = true; // true when on Home without any interactive popup
    private isHomeInteractive = false; // Home popup open
    private logQueue: LogQueueItem[] = [];
    private flushing = false;
    private lastMenuTrack: { menuId: string; at: number } | null = null;
    private lastSearchKeywordByMenuId = new Map<string, string>();
    private lastPoiTrackingContext: { trackingId: string; keyword: string; poiContentId: string; poiName?: Record<string, string> } | null = null;

    // Helpers to get machineId, locale, context
    private getMachineId(): string | null {
        const config = useConfigStore.getState().config;
        return config?.server?.machine?.id ?? null;
    }

    private isTestEnvironment(): boolean {
        const config = useConfigStore.getState().config;
        return config?.server?.machine?.environment === 'test';
    }

    private getCompanyId(): string {
        const config = useConfigStore.getState().config;
        return config?.server?.company?.companyId ?? '0';
    }

    private getLocale(): string {
        const config = useConfigStore.getState().config;
        return config?.ui?.language ?? 'ko';
    }

    private getContext(): ContextIds {
        const config = useConfigStore.getState().config;
        const buildingId = config?.server?.building?.id ?? '0';
        const storeId = config?.server?.store?.id ?? '0';
        const floorId = config?.server?.floor?.id ?? '0';
        const poiList = config.server?.store?.buildings?.map((building) => building.poiContents).flat() as PoiContent[] | null;
        return { buildingId, storeId, floorId, poiList: poiList ?? null };
    }

    private resolvePoiName(poi?: PoiLocationRef): Record<string, string> | null {
        if (!poi?.name) return null;
        return poi.name|| null;
    }

    private resolvePoiLocationIds(poi: PoiLocationRef): { floorId: string; buildingId: string } {
        const config = useConfigStore.getState().config;
        const buildings = config?.server?.store?.buildings ?? [];
        const building = buildings.find((item) => item.studioBuildingId === poi.studioBuildingId);
        const floor = building?.floors?.find((item) => item.floor_code === poi.studioFloorId);

        return {
            buildingId: building?.studioBuildingId ?? '0',
            floorId: floor?.studioFloorId ?? '0',
        };
    }

    private findPoiById(poiId: string): PoiContent | undefined {
        const config = useConfigStore.getState().config;
        const buildings = config?.server?.store?.buildings ?? [];

        for (const building of buildings) {
            const buildingPois = [
                ...(building.poiContents ?? []),
                ...(building.filterPoiContents?.shop ?? []),
                ...(building.filterPoiContents?.fnb ?? []),
                ...(building.filterPoiContents?.facility ?? []),
                ...(building.filterPoiContents?.otherUtility ?? []),
            ];
            const found = buildingPois.find((poi) => poi.id === poiId || poi.studioPoiId === poiId);
            if (found) return found;
        }

        const lang = config?.ui?.language ?? 'ko';
        const mainLang = config?.ui?.mainLanguage ?? lang;
        const menus = config?.server?.buildingMenus?.[lang] ?? config?.server?.buildingMenus?.[mainLang] ?? [];

        for (const menu of menus) {
            const menuPois = [
                ...(menu.poiContents ?? []),
                ...(menu.poiContentBuildings?.flatMap((item) => item.poiContents ?? []) ?? []),
            ];
            const found = menuPois.find((poi) => poi.id === poiId || poi.studioPoiId === poiId);
            if (found) return found;
        }

        return this.getContext().poiList?.find((poi) => poi.id === poiId || poi.studioPoiId === poiId);
    }

    private getCurrentMenuTypeFromPath(pathname: string): BuildingMenuType | null {
        if (pathname.startsWith(PATHS.FLOOR_GUIDE.INDEX)) return BuildingMenuType.FLOOR;
        if (pathname.startsWith(PATHS.SHOP.INDEX)) return BuildingMenuType.SEARCH;
        for (const base of Object.values(PATHS.CUSTOM_MENU)) {
            if (pathname === base || pathname.startsWith(`${base}/`)) {
                return BuildingMenuType.CUSTOM_MENU;
            }
        }
        // if (pathname.startsWith(PATHS.FNB.INDEX)) return BuildingMenuType.FNB;
        // if (pathname.startsWith(PATHS.EVENTS.INDEX)) return BuildingMenuType.EVENT;
        // if (pathname.startsWith(PATHS.PARKING.INDEX)) return BuildingMenuType.PARKING;
        // if (pathname.startsWith(PATHS.FACILITY.INDEX)) return BuildingMenuType.FACILITIES;
        // if (pathname.startsWith(PATHS.FAQ.INDEX)) return BuildingMenuType.FAQ;
        return null;
    }

    private resolveMenuIdByType(type: BuildingMenuType | null, pathname?: string): string | null {
        if (!type) return null;
        if (type === BuildingMenuType.CUSTOM_MENU && pathname) {
            for (const base of Object.values(PATHS.CUSTOM_MENU)) {
                if (pathname.startsWith(`${base}/`)) {
                    const rest = pathname.slice(base.length + 1);
                    if (rest && !rest.includes('/')) {
                        return rest;
                    }
                }
            }
        }
        const config = useConfigStore.getState().config;
        const lang = config?.ui?.language ?? 'ko';
        const mainLang = config?.ui?.mainLanguage ?? lang;
        const menus = config?.server?.buildingMenus?.[lang] ?? config?.server?.buildingMenus?.[mainLang] ?? [];
        const found = menus.find((m) => m.menuType === type);
        return found?.id ?? null;
    }

    private shouldSkipDuplicateMenuTrack(menuId: string): boolean {
        const now = Date.now();
        if (this.lastMenuTrack?.menuId === menuId && now - this.lastMenuTrack.at < 500) {
            return true;
        }
        this.lastMenuTrack = { menuId, at: now };
        return false;
    }

    private getCurrentMenuId(): string | null {
        const pathname = window.location.pathname;
        return this.resolveMenuIdByType(this.getCurrentMenuTypeFromPath(pathname), pathname);
    }

    // Session lifecycle
    private startSessionIfNeeded() {
        if (this.session?.isActive) return;
        const now = Date.now();
        this.session = {
            isActive: true,
            sessionStartAt: now,
            lastActiveStartAt: now,
            totalActiveMs: 0,
            lastLocale: this.getLocale(),
            lastContext: this.getContext(),
        };
    }

    private startActiveSegment() {
        if (!this.session || this.session.lastActiveStartAt) return;
        this.session.lastActiveStartAt = Date.now();
    }

    private stopActiveSegment() {
        if (!this.session || !this.session.lastActiveStartAt) return;
        const now = Date.now();
        this.session.totalActiveMs += Math.max(0, now - this.session.lastActiveStartAt);
        this.session.lastActiveStartAt = null;
        this.session.lastContext = this.getContext();
        this.session.lastLocale = this.getLocale();
    }

    private generateTrackingId(): string {
        return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    }

    private resetSession() {
        this.session = null;
        this.isHomeInteractive = false;
        this.isInHomeBase = true;
        this.lastSearchKeywordByMenuId.clear();
        this.lastPoiTrackingContext = null;
    }

    private getSearchKeywordForMenu(menuId: string): string {
        return this.lastSearchKeywordByMenuId.get(menuId) ?? '';
    }

    clearSearchKeyword(menuId?: string) {
        const resolvedMenuId = menuId ?? this.getCurrentMenuId();
        if (!resolvedMenuId) return;
        this.lastSearchKeywordByMenuId.delete(resolvedMenuId);
    }

    // Public wiring APIs
    onRouteChange(prevPathname: string, nextPathname: string) {
        const wasHomeBase = prevPathname === PATHS.HOME.INDEX && !this.isHomeInteractive;
        const isHomeBaseNow = nextPathname === PATHS.HOME.INDEX && !this.isHomeInteractive;

        // Leaving Home base to a different page
        if (wasHomeBase && nextPathname !== PATHS.HOME.INDEX) {
            this.isInHomeBase = false;
            this.startSessionIfNeeded();
            this.startActiveSegment();

            const menuId = this.resolveMenuIdByType(this.getCurrentMenuTypeFromPath(nextPathname), nextPathname);
            if (menuId) {
                this.trackMenu(menuId);
            }
            return;
        }

        // Navigating back to Home manually from another page
        if (!wasHomeBase && isHomeBaseNow) {
            // Close active segment but keep session
            this.stopActiveSegment();
            this.isInHomeBase = true;
            return;
        }
    }

    // Called when countdown timeout triggers navigate Home (from non-Home pages)
    onTimeoutNavigateHome() {
        if (!this.session) return;
        this.stopActiveSegment();

        const machineId = this.getMachineId();
        if (!machineId) {
            this.resetSession();
            return;
        }

        const { buildingId, storeId, floorId } = this.session.lastContext ?? this.getContext();
        const companyId = this.getCompanyId();
        const startIso = new Date(this.session.sessionStartAt).toISOString();
        const endIso = new Date().toISOString();
        const durationSec = Math.floor(this.session.totalActiveMs / 1000);
        const payload: SessionLog = {
            machineId,
            companyId,
            floorId,
            storeId,
            buildingId,
            duration: durationSec,
            locale: this.session.lastLocale,
            sessionStartTime: startIso,
            sessionEndTime: endIso,
        };
        this.enqueue({ type: 'session', payload });

        // Also count one language usage for this session's locale
        const langPayload: LanguageLog = { machineId, companyId, floorId, storeId, buildingId, locale: this.session.lastLocale };
        this.enqueue({ type: 'language', payload: langPayload });
        this.resetSession();
    }

    // Home popup open/close
    onHomePopupOpen() {
        this.isHomeInteractive = true;
        this.isInHomeBase = false;
        this.startSessionIfNeeded();
        this.startActiveSegment();
    }

    onHomePopupClose() {
        this.stopActiveSegment();
        this.isHomeInteractive = false;
        this.isInHomeBase = true;
    }
    onHomeMenuHiddenByNavigation() {
        this.isHomeInteractive = false;
    }

    // Called when timeout occurs while popup is open at Home
    onTimeoutFromHomePopup() {
        this.onTimeoutNavigateHome();
    }

    // Language change: count language usage (via menu log or dedicated endpoint if later). Do not end session.
    onLocaleChanged() {
        // Log language usage immediately when user changes language (even on Home base)
        const machineId = this.getMachineId();
        if (!machineId) return;
        const { buildingId, storeId, floorId } = this.getContext();
        const companyId = this.getCompanyId();
        const locale = this.getLocale();
        const payload: LanguageLog = { machineId, companyId, buildingId, storeId, floorId, locale };
        this.enqueue({ type: 'language', payload });

        // Update session tagging
        if (this.session) {
            this.session.lastLocale = locale;
        }
    }

    // Tracking primitives
    private resolveMenuGroupUuid(menuId: string): string | undefined {
        const config = useConfigStore.getState().config;
        const lang = config?.ui?.language ?? 'ko';
        const mainLang = config?.ui?.mainLanguage ?? lang;
        const menus = config?.server?.buildingMenus?.[lang] ?? config?.server?.buildingMenus?.[mainLang] ?? [];
        const found = menus.find((m) => m.id === menuId);
        return found?.menuGroupUuid ?? undefined;
    }

    trackMenu(menuId?: string, context?: Partial<ContextIds>) {
        const machineId = this.getMachineId();
        if (!machineId) return;
        const resolvedMenuId = menuId || this.getCurrentMenuId();
        if (!resolvedMenuId) return;
        if (this.shouldSkipDuplicateMenuTrack(resolvedMenuId)) return;
        const { buildingId, storeId, floorId } = { ...this.getContext(), ...context } as ContextIds;
        const companyId = this.getCompanyId();
        const menuGroupUuid = this.resolveMenuGroupUuid(resolvedMenuId);
        const payload: MenuLog = { machineId, companyId, buildingId, storeId, floorId, menuId: resolvedMenuId, menuGroupUuid, locale: this.getLocale() };
        this.enqueue({ type: 'menu', payload });
    }

    trackMenuByRoute(targetRoute: string, menuId?: string, context?: Partial<ContextIds>) {
        const resolvedMenuId = menuId || this.resolveMenuIdByType(this.getCurrentMenuTypeFromPath(targetRoute), targetRoute);
        if (!resolvedMenuId) return;
        this.trackMenu(resolvedMenuId, context);
    }

    trackPoi(poiId: string, menuId?: string, poi?: PoiLocationRef) {
        let correctPoiId: string;
        let poiForLocation: PoiLocationRef | undefined = poi;

        if (!isNaN(Number(poiId))) {
            correctPoiId = poiId;
            poiForLocation = poiForLocation ?? this.findPoiById(poiId);
        } else {
            const foundPoi = poiForLocation ?? this.findPoiById(poiId);
            if (!foundPoi) {
                return;
            }
            correctPoiId = foundPoi.id;
            poiForLocation = foundPoi;
        }

        if (this.isInHomeBase) return;
        const machineId = this.getMachineId();
        if (!machineId) return;
        const resolvedMenuId = menuId || this.getCurrentMenuId();
        if (!resolvedMenuId) return;

        const { storeId } = this.getContext();
        const { buildingId, floorId } = poiForLocation ? this.resolvePoiLocationIds(poiForLocation) : { buildingId: '0', floorId: '0' };
        const companyId = this.getCompanyId();
        const keyword = this.getSearchKeywordForMenu(resolvedMenuId);
        const trackingId = this.generateTrackingId();
        const poiName = this.resolvePoiName(poiForLocation as PoiLocationRef);
        this.lastPoiTrackingContext = { trackingId, keyword, poiContentId: correctPoiId, poiName };
        const payload: PoiLog = {
            machineId,
            companyId,
            buildingId:  poi?.studioBuildingId,
            storeId,
            floorId: poi?.studioFloorId,
            poiContentId: correctPoiId,
            poiId: poi?.studioPoiId,
            poiName: poiName || undefined,
            menuId: resolvedMenuId,
            keyword,
            trackingId,
            locale: this.getLocale(),
        };
        this.enqueue({ type: 'poi', payload });
    }

    trackKeyword(keyword: string, menuId?: string, context?: Partial<ContextIds>) {
        if (this.isInHomeBase) return;
        const machineId = this.getMachineId();
        if (!machineId) return;
        const resolvedMenuId = menuId || this.getCurrentMenuId();
        if (!resolvedMenuId) return;
        this.lastSearchKeywordByMenuId.set(resolvedMenuId, keyword);
        const { buildingId, storeId, floorId } = { ...this.getContext(), ...context } as ContextIds;
        const companyId = this.getCompanyId();
        const payload: KeywordLog = { machineId, companyId, buildingId, storeId, floorId, menuId: resolvedMenuId, keyword, locale: this.getLocale() };
        this.enqueue({ type: 'keyword', payload });
    }

    trackNavigation(params: {
        startBuildingId: string;
        startFloorId: string;
        startPosition: { x: number; y: number };
        endBuildingId: string;
        endFloorId: string;
        endPoiId: string;
        endPosition: { x: number; y: number };
        menuId?: string;
        transportMode?: string;
    }) {
        const machineId = this.getMachineId();
        if (!machineId) return;
        const resolvedMenuId = params.menuId || this.getCurrentMenuId();
        if (!resolvedMenuId) return;
        const { storeId } = this.getContext();
        const companyId = this.getCompanyId();
        const keyword = this.lastPoiTrackingContext?.keyword || '';
        const trackingId = this.lastPoiTrackingContext?.trackingId || '';
        const poiContentId = this.lastPoiTrackingContext?.poiContentId || '';
        const poiName = this.lastPoiTrackingContext?.poiName || '';
        const payload: NavigationLog = {
            machineId,
            companyId,
            storeId,
            locale: this.getLocale(),
            menuId: resolvedMenuId,
            poiContentId,
            poiName: poiName || undefined,
            startBuildingId: params.startBuildingId,
            startFloorId: params.startFloorId,
            startPosition: params.startPosition,
            endBuildingId: params.endBuildingId,
            endFloorId: params.endFloorId,
            endPoiId: params.endPoiId,
            endPosition: params.endPosition,
            ...(params.transportMode ? { transportMode: params.transportMode } : {}),
            ...(keyword ? { keyword } : {}),
            ...(trackingId ? { trackingId } : {}),
        };
        this.enqueue({ type: 'navigation', payload });
    }

    // Queue and flush with simple retry
    private enqueue(item: LogQueueItem) {
        if (this.isTestEnvironment()) return;
        this.logQueue.push(item);
        this.flushSoon();
    }

    private flushSoon() {
        if (this.flushing) return;
        this.flushing = true;
        // microtask
        Promise.resolve()
            .then(() => this.flush())
            .finally(() => {
                this.flushing = false;
            });
    }

    private async flush() {
        const queue = this.logQueue.splice(0, this.logQueue.length);
        for (const item of queue) {
            try {
                if (item.type === 'session') await saveSessionLog(item.payload);
                else if (item.type === 'menu') await saveMenuLog(item.payload);
                else if (item.type === 'poi') await savePoiLog(item.payload);
                else if (item.type === 'keyword') await saveKeywordLog(item.payload);
                else if (item.type === 'language') await saveLanguageLog(item.payload);
                else if (item.type === 'navigation') await saveNavigationLog(item.payload);
            } catch {
                // simple retry: push back to queue tail
                this.logQueue.push(item);
            }
        }
    }
}

export const TrackingService = TrackingServiceImpl.instance;
